import type { StatCell, Unit } from "@sins2-atlas/game-data";
import { exoticFabricationTotal, RACES, UNIT_KINDS } from "@sins2-atlas/game-data";
import { Link } from "@tanstack/react-router";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type RowData,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { clsx } from "clsx";
import { Fragment, useMemo, useState } from "react";
import { compareToggle } from "../lib/compare";
import { dataset, exoticCountsText } from "../lib/dataset";
import { craftCountText, disclosureById } from "../lib/disclosures";
import { BASE_FRAME, type StatFrame } from "../lib/frame";
import { frameSquadrons } from "../lib/frame-fold";
import { cellSortValue } from "../lib/stat-cell";
import {
  headerLabel,
  type LineColumn,
  lineColumn,
  PROVIDES_NOTE,
  type StatRowSpec,
  statModelFor,
  type ViewKind,
} from "../lib/stat-model";
import { unitLinkSearch } from "../lib/unit-link";
import {
  DLC_META,
  exclusiveFaction,
  OBTAINED_META,
  RACE_META,
  UNIT_KIND_LABELS,
} from "../lib/vocabulary";
import { ArtImage } from "./art-image";
import { Chip } from "./chip";
import { ExoticCounts } from "./exotic-counts";
import { ProvidesCell } from "./provides-line";
import { StatCellView } from "./stat-cell";
import { WeaponTable } from "./weapon-table";

/** A StatCell column: sorts by value with every non-Computed state pinned last in both
 * directions (sortUndefined), renders through the quiet-glyph view. */
function statColumn(
  id: string,
  header: string,
  accessorFn: (unit: Unit) => StatCell,
  options: { format?: (value: number) => string } = {},
): ColumnDef<Unit> {
  const disclosure = disclosureById.get(id);
  return {
    id,
    header,
    accessorFn: (unit) => cellSortValue(accessorFn(unit)),
    sortDescFirst: true,
    sortUndefined: "last",
    meta: {
      numeric: true,
      sub: disclosure?.sub,
      headerTitle: disclosure?.formula,
    },
    cell: (ctx) => <StatCellView cell={accessorFn(ctx.row.original)} format={options.format} />,
  };
}

/** One model row as a sortable column. The dense table is the one surface that abbreviates,
 * so it is the one that reads `shortLabel`. */
function modelColumn(row: StatRowSpec, frame: StatFrame): ColumnDef<Unit> {
  return statColumn(row.id, headerLabel(row), (unit) => row.cell(unit, frame), {
    ...(row.format === undefined ? {} : { format: row.format }),
  });
}

/** A list-valued group (Provides) as ONE column, the model's rule for this surface
 * (`lineColumn`, ADR 0008 addendum): the group's own word as the header, each cell holding
 * that structure's own lines with the game's labels beside their values, text that wraps,
 * an empty cell where a structure states nothing, and no sorting — a list of facts has no
 * order a sort could honour. */
function lineColumnDef(column: LineColumn, frame: StatFrame): ColumnDef<Unit> {
  return {
    id: column.id,
    header: column.label,
    enableSorting: false,
    meta: { headerTitle: PROVIDES_NOTE, wrap: true },
    cell: (ctx) => <ProvidesCell lines={column.linesOf(ctx.row.original, frame)} />,
  };
}

const identityColumns: ColumnDef<Unit>[] = [
  {
    id: "name",
    header: "Name",
    accessorFn: (u) => u.name,
    sortDescFirst: false,
    cell: (ctx) => {
      const unit = ctx.row.original;
      const label = exclusiveFaction(unit);
      const obtained = OBTAINED_META[unit.obtained];
      const meta = ctx.table.options.meta;
      const expanded = meta?.expandedIds.has(unit.id);
      const compare = meta?.compare ?? [];
      const { inSet, full, fullTitle, commit } = compareToggle(compare, unit.id, (next) =>
        meta?.onCompareChange(next),
      );
      return (
        <span className="flex items-center gap-2">
          {/* The comparison tick (design prototype: a 12px checkbox at the row's head).
              A row click expands the row, so the tick must stop propagation. The
              accessible name stays CONSTANT while aria-pressed carries the state (the
              APG toggle rule); aria-disabled instead of disabled keeps a full-set tick
              focusable, so its title can still explain why it refuses. */}
          <button
            type="button"
            aria-pressed={inSet}
            aria-disabled={full}
            aria-label={`Compare ${unit.name}`}
            title={inSet ? "Remove from comparison" : full ? fullTitle : "Add to comparison"}
            onClick={(event) => {
              event.stopPropagation();
              commit();
            }}
            className={clsx(
              "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border text-[9px] leading-none",
              inSet
                ? "cursor-pointer border-ink bg-ink text-bg"
                : "border-line-strong text-transparent",
              !inSet && !full && "cursor-pointer hover:border-muted",
              full && "cursor-not-allowed opacity-40",
            )}
          >
            ✓
          </button>
          {/* The disclosure triangle is a real button (review 2026-09-08): the row click is the
              pointer convenience, and this is what reaches the expanded weapons table from the
              keyboard. aria-expanded carries the state; the name stays constant. */}
          <button
            type="button"
            aria-expanded={expanded === true}
            aria-label={`Details for ${unit.name}`}
            onClick={(event) => {
              event.stopPropagation();
              meta?.onToggleExpanded(unit.id);
            }}
            className="inline-block w-4 cursor-pointer rounded text-[13px] text-ink-3 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-link"
          >
            {expanded ? "▾" : "▸"}
          </button>
          {/* Fixed 48×24 box, contain-fit: hud icons are usually 85×40 but not always
              (the envoy's is 47×33), and rows must stay aligned either way. */}
          <span className="flex h-6 w-12 shrink-0 items-center justify-center">
            <ArtImage asset={unit.art.hudIcon} alt="" className="max-h-6 max-w-12 object-contain" />
          </span>
          <Link
            to="/unit/$unitId"
            params={{ unitId: unit.id }}
            // The active frame and the compare set follow the link, like every
            // unit-level link.
            search={unitLinkSearch(meta?.frame ?? BASE_FRAME, meta?.faction ?? {}, compare)}
            className="font-medium text-ink underline decoration-line-strong underline-offset-2 hover:text-link hover:decoration-link"
            onClick={(event) => event.stopPropagation()}
            title={`Open the ${unit.name} page`}
          >
            {unit.name}
          </Link>
          {label && (
            <Chip
              label={label.short}
              title={`Only ${label.full} can obtain this unit`}
              className="text-muted-2"
            />
          )}
          {obtained.chipped && <Chip label={obtained.label} title={obtained.title} />}
          {unit.dlc && (
            <Chip
              label="DLC"
              title={DLC_META[unit.dlc].title}
              className="border-unverified/50 text-unverified"
            />
          )}
        </span>
      );
    },
  },
  {
    id: "race",
    header: "Race",
    accessorFn: (u) => u.race,
    sortDescFirst: false,
    // Sort in the fixed race order (TEC, Advent, Vasari, Eidolon), then by name within a race.
    sortingFn: (a, b) =>
      RACES.indexOf(a.original.race) - RACES.indexOf(b.original.race) ||
      a.original.name.localeCompare(b.original.name),
    cell: (ctx) => {
      const race = RACE_META[ctx.row.original.race];
      return (
        <span className="inline-flex items-baseline gap-1.5">
          <span aria-hidden className={clsx("text-[9px]", race.textClass)}>
            {race.sigil}
          </span>
          <span className="text-ink-3">{race.label}</span>
        </span>
      );
    },
  },
  {
    id: "class",
    header: "Class",
    accessorFn: (u) => u.kind,
    sortDescFirst: false,
    // Sort by the size ladder (strikecraft → titan, then structures and starbases).
    sortingFn: (a, b) => UNIT_KINDS.indexOf(a.original.kind) - UNIT_KINDS.indexOf(b.original.kind),
    cell: (ctx) => <span className="text-ink-3">{UNIT_KIND_LABELS[ctx.row.original.kind]}</span>,
  },
];

const exoticsColumn: ColumnDef<Unit> = {
  id: "exotics",
  header: "Exotics",
  // Sorts by the total fabrication price of the unit's exotics; units without any pin
  // last in both directions like non-Computed stat cells.
  accessorFn: (u) =>
    u.exoticPrice.length === 0
      ? undefined
      : (() => {
          const total = exoticFabricationTotal(u.exoticPrice);
          return total.credits + total.metal + total.crystal;
        })(),
  sortDescFirst: true,
  sortUndefined: "last",
  meta: {
    numeric: true,
    headerTitle:
      "Exotic components required to build the unit. Sorts by their total fabrication price.",
  },
  cell: (ctx) => <ExoticCounts unit={ctx.row.original} />,
};

// What this table adds to a shared model group, by group title. The exotics column is the
// roster's alone — the other surfaces state exotics as a row of names, which a sortable
// numeric column cannot be. Additions append to their group; they never reorder it. It
// attaches to Cost, which since 2026-09-07 holds the game's three resource rows AND the
// supply and build time the old "Fleet" group carried — the exotics a hull is built from
// are a price, so the column stays where those three are.
export const EXTRA_COLUMNS: Record<string, ColumnDef<Unit>[]> = {
  Cost: [exoticsColumn],
};

/** Cloak Detection and Cloaking under ONE section header (user, 2026-09-08: "leave the
 * columns separate, but have 1 section header above them to match the card"). The entity
 * page draws the two model groups as one card (unit.tsx), so the table heads them with one
 * word: "Cloaking", the game's own, and the subject both columns are about — Cloak Detection
 * is the ability to see through it (CONTEXT.md). The model still declares two groups in Game
 * Order and the two columns keep their ids, labels and order; only the header row joins.
 * Found by the game's words rather than by index, like the entity page. A view drawing only
 * one of the two (Structures, where nothing cloaks) keeps that group's own word. */
function joinCloakSections(sections: ColumnDef<Unit>[]): ColumnDef<Unit>[] {
  const isCloak = (section: ColumnDef<Unit>) =>
    section.header === dataset.cloakLabels.detection ||
    section.header === dataset.cloakLabels.cloaking;
  const cloak = sections.filter(isCloak);
  if (cloak.length < 2) return sections;
  const joined: ColumnDef<Unit> = {
    id: `group-${dataset.cloakLabels.cloaking.toLowerCase()}`,
    header: dataset.cloakLabels.cloaking,
    columns: cloak.flatMap((section) => ("columns" in section ? (section.columns ?? []) : [])),
  };
  const first = sections.findIndex(isCloak);
  return sections.flatMap((section, index) =>
    index === first ? [joined] : isCloak(section) ? [] : [section],
  );
}

// The section groups the user reads the table by: a second header row above the sortable
// columns, straight off the stat model, so this table cannot drift from the entity page and
// the comparison again. The model is asked for the view's kind (ADR 0008): the Structures
// table has no Bombing, Pop. damage, Supply or DPS / supply column because the model says
// those rows are ships-only, not because this table knows so. Group boundaries draw a left
// rule via meta.groupStart on the group's first column. Columns build per frame: each row's
// `cell` closes over the active frame. A list-valued group (Provides) is ONE column here
// (the model's lineColumn rule, ADR 0008 addendum) rather than the one-per-label rows the
// entity page and the comparison render; it is read over the VISIBLE `units`, so the table
// rebuilds as the facets change and a set of structures stating nothing drops the group
// rather than drawing a heading over nothing.
export function buildColumns(
  frame: StatFrame,
  kind: ViewKind,
  units: readonly Unit[],
): ColumnDef<Unit>[] {
  // A list-valued group is read over the units AS DRAWN — each with the frame this table is
  // rendering it under (Issue 48: a Provides line can be a Loadout question). One frame here,
  // the roster views declaring no `?items=`, so every pair carries the same one.
  const drawn = units.map((unit) => ({ unit, frame }));
  const sections = statModelFor([kind]).flatMap((group) => {
    const lines = lineColumn(group, drawn);
    const groupColumns = [
      ...group.rows.map((row) => modelColumn(row, frame)),
      ...(lines === undefined ? [] : [lineColumnDef(lines, frame)]),
      ...(EXTRA_COLUMNS[group.title] ?? []),
    ];
    return groupColumns.length === 0
      ? []
      : [
          {
            id: `group-${group.title.toLowerCase()}`,
            header: group.title,
            columns: groupColumns,
          },
        ];
  });
  const columns: ColumnDef<Unit>[] = [
    { id: "group-unit", header: "", columns: identityColumns },
    ...joinCloakSections(sections),
  ];
  // Flag each non-first group's first column so headers and cells draw the section rule.
  for (const group of columns.slice(1)) {
    const first = "columns" in group ? group.columns?.[0] : undefined;
    if (first) first.meta = { ...first.meta, groupStart: true };
  }
  return columns;
}

// Module augmentation rather than a cast at every read (the router does the same for its own
// Register interface): TanStack types `columnDef.meta` and `options.meta` as these interfaces,
// so declaring them once makes all five former `as ColumnMeta` / `as TableMeta` assertions
// unnecessary — and a typo in a meta key now fails to compile instead of reading `undefined`.
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    numeric?: boolean;
    sub?: string;
    headerTitle?: string;
    groupStart?: boolean;
    /** Text cells that may wrap (a Provides line lists up to six build kinds). */
    wrap?: boolean;
  }
  interface TableMeta<TData extends RowData> {
    expandedIds: Set<string>;
    frame: StatFrame;
    /** The site-wide Faction Selection as it rides a row's link (ADR 0010). */
    faction: { faction?: string };
    compare: string[];
    onCompareChange: (next: string[]) => void;
    onToggleExpanded: (id: string) => void;
  }
}

function UnitDetail({ unit, frame }: { unit: Unit; frame: StatFrame }) {
  // The hangar follows the frame since Issue 48 (a hangar Ship Item grows the shared
  // capacity). This surface carries no Loadout, so it reads the hull's own figures.
  const squadrons = frameSquadrons(unit, frame);
  return (
    <div className="flex flex-col gap-3 px-6 py-4">
      <WeaponTable unit={unit} frame={frame} />
      {squadrons && squadrons.capacity > 0 && (
        <p className="text-[12.5px] text-muted-2">
          Fields {squadrons.capacity} strikecraft squadron
          {squadrons.capacity > 1 ? "s" : ""}:{" "}
          {squadrons.options.map((o) => craftCountText(o, o.name)).join(" or ")}. Their damage is
          not counted in this ship's DPS.
        </p>
      )}
      {unit.abilities.length > 0 && (
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-2">
            Abilities
          </span>
          {unit.abilities.map((ability) => (
            <span
              key={ability.id}
              className="rounded border border-line px-1.5 py-0.5 text-[12.5px] text-ink-2"
              title={ability.description}
            >
              {ability.name}
            </span>
          ))}
        </div>
      )}
      {unit.exoticPrice.length > 0 && (
        <p className="text-[12.5px] text-muted-2">
          Also costs exotics: {exoticCountsText(unit.exoticPrice)}. The credits, metal and crystal
          columns show the direct price; DPS/cost and Cost/HP also count these exotics at their
          fabrication price.
        </p>
      )}
    </div>
  );
}

export function RosterTable({
  units,
  kind,
  sorting,
  onSortingChange,
  frame,
  faction,
  compare,
  onCompareChange,
}: {
  units: Unit[];
  /** The view being rendered — which rows of the one model become columns. */
  kind: ViewKind;
  sorting: SortingState;
  onSortingChange: (next: SortingState) => void;
  frame: StatFrame;
  /** The Faction Selection's search params, spread onto every row's unit link. */
  faction: { faction?: string };
  compare: string[];
  onCompareChange: (next: string[]) => void;
}) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const columns = useMemo(() => buildColumns(frame, kind, units), [frame, kind, units]);
  const table = useReactTable({
    data: units,
    columns,
    state: { sorting },
    // Checked against the augmented TableMeta by the option's own type — no `satisfies` needed.
    meta: {
      expandedIds,
      frame,
      faction,
      compare,
      onCompareChange,
      onToggleExpanded: toggleExpanded,
    },
    onSortingChange: (updater) => {
      onSortingChange(typeof updater === "function" ? updater(sorting) : updater);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface">
      <table className="w-full min-w-[1400px] border-collapse text-[15px] [font-variant-numeric:tabular-nums]">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-line-strong">
              {headerGroup.headers.map((header) => {
                // The section-group row: quiet labels spanning their columns, no sorting.
                if (header.subHeaders.length > 0 || header.isPlaceholder) {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={clsx(
                        "select-none pt-2.5 pb-0.5 text-center font-condensed text-[10.5px] font-semibold uppercase tracking-[0.18em] text-muted",
                        header.column.columns[0]?.columnDef.meta?.groupStart &&
                          "border-l border-line",
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                }
                const sorted = header.column.getIsSorted();
                const meta = header.column.columnDef.meta;
                // A Provides column cannot sort: no pointer cursor, no handler, no
                // aria-sort claim (undefined omits the attribute).
                const sortable = header.column.getCanSort();
                return (
                  <th
                    key={header.id}
                    className={clsx(
                      "select-none whitespace-nowrap px-3 py-2 align-top font-mono text-[11.5px] font-medium uppercase tracking-[0.1em]",
                      sortable && "cursor-pointer",
                      meta?.numeric ? "text-right" : "text-left",
                      sorted ? "bg-raised text-ink" : "text-ink-3",
                      sortable && !sorted && "hover:text-ink-2",
                      meta?.groupStart && "border-l border-line",
                    )}
                    onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
                    title={meta?.headerTitle}
                    aria-sort={
                      !sortable
                        ? undefined
                        : sorted === "asc"
                          ? "ascending"
                          : sorted === "desc"
                            ? "descending"
                            : "none"
                    }
                  >
                    <span>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <span className="ml-1 inline-block w-2 text-[9px]">
                        {sorted === "asc" ? "↑" : sorted === "desc" ? "↓" : ""}
                      </span>
                    </span>
                    {meta?.sub && (
                      <span className="block text-[10px] font-normal normal-case tracking-normal text-muted">
                        {meta.sub}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <Fragment key={row.id}>
              <tr
                className="cursor-pointer border-t border-line-faint hover:bg-raised/50"
                onClick={() => toggleExpanded(row.original.id)}
              >
                {row.getVisibleCells().map((cell) => {
                  const numeric = cell.column.columnDef.meta?.numeric === true;
                  const sorted = cell.column.getIsSorted() !== false;
                  const wrap = cell.column.columnDef.meta?.wrap === true;
                  return (
                    <td
                      key={cell.id}
                      className={clsx(
                        "px-3 py-2.5",
                        wrap
                          ? "min-w-[180px] max-w-[260px] whitespace-normal"
                          : "whitespace-nowrap",
                        numeric && "text-right font-mono",
                        sorted && "bg-raised/40",
                        cell.column.columnDef.meta?.groupStart && "border-l border-line",
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
              {expandedIds.has(row.original.id) && (
                <tr className="border-t border-line-faint bg-surface-deep">
                  <td colSpan={table.getVisibleLeafColumns().length}>
                    <UnitDetail unit={row.original} frame={frame} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
